import { urlParamsEnabled } from "./urlParams";

export interface ClientAttributes {
  /**
   * The number of lines to display if showCompleteTranscript is set to false
   * @default undefined
   */
  lineCount?: number;

  /**
   * The live event's key for fetching properly out of S3
   * @default undefined
   */
  liveAsrUniqueKey?: string;

  /**
   * The summarization key for fetching summarization transcript out of S3
   * @default undefined
   */
  summarizationKey?: string;

  /**
   * Whether or not to show the Accessibility Menu
   * @default false
   */
  showAccessibilityMenu: boolean;

  /**
   * Whether or not the complete transcript should be displayed
   * @default false
   */
  showCompleteTranscript: boolean;

  /**
   * Whether or not the embed screen is on a static url
   * @default false
   */
  pollAfterEvent: boolean;

  /**
   * The container height displaying the transcript (in pixels)
   *
   * If it is undefined or set to 0, then the viewport height is used
   * @default undefined
   * @deprecated This is no longer set in the rails app's embed code
   */
  transcriptContainerHeight?: number;

  /**
   * Whether or not the captions should be displayed in uppercase
   * @default true
   */
  useUppercase: boolean;

  /**
   * Number of seconds to delay displaying captions after they are produced.
   * When set, captions are held back until sent_to_target_at + delay <= now.
   * @default undefined
   */
  delaySeconds?: number;

  /**
   * The default caption language (browser_navigator_code) for a JS embed, used
   * when the page cannot pass `?lang=`. A `?lang=` query param takes precedence.
   * Validated against the event manifest, falling back to source when absent or
   * unsupported.
   * @default undefined
   */
  defaultLanguage?: string;
}

export function getClientAttributes(): ClientAttributes {
  const liveAsrClient = document.getElementById("live-asr-client");
  const getFromUrl = urlParamsEnabled();
  const params = new URLSearchParams(window.location.search);

  function getDefinedValue(paramsName: string, attributeName: string) {
    return getFromUrl
      ? params.get(paramsName) || liveAsrClient?.getAttribute(attributeName)
      : liveAsrClient?.getAttribute(attributeName);
  }

  const definedLineCount = getDefinedValue("line_count", "data-line-count");

  const definedTranscriptContainerHeight = getDefinedValue(
    "container_height",
    "transcript-container-height"
  );

  const summarizationKey =
    getDefinedValue("summarization_key", "data-summarization-key") || undefined;

  const definedDelay = getDefinedValue("delay", "data-delay");

  return {
    lineCount: definedLineCount ? parseInt(definedLineCount, 10) : undefined,
    liveAsrUniqueKey: getDefinedValue("key", "data-key") || undefined,
    summarizationKey,
    showAccessibilityMenu:
      getDefinedValue(
        "show_accessibility_menu",
        "data-show-accessibility-menu"
      ) === "true" || Boolean(summarizationKey),
    showCompleteTranscript:
      getDefinedValue(
        "show_complete_transcript",
        "data-show-complete-transcript"
      ) === "true",
    pollAfterEvent:
      getDefinedValue("poll_after_event", "data-poll-after-event") === "true",
    transcriptContainerHeight: definedTranscriptContainerHeight
      ? parseInt(definedTranscriptContainerHeight, 10)
      : undefined,
    useUppercase: getDefinedValue("use_uppercase", "data-uppercase") !== "0",
    delaySeconds: definedDelay ? parseInt(definedDelay, 10) : undefined,
    defaultLanguage: getDefinedValue("lang", "data-lang") || undefined,
  };
}
